/**
 * Admin — Notification config.
 * Route: /admin/notifications-config
 *
 * Tabs:
 *  1) Ufficio ordini      — destinatari ufficio + fallback commerciale (invariato)
 *  2) Eventi email        — Mar 2026 fase 1: editor template/recipient/CC per evento,
 *                           preview + invio email di prova. Il routing operativo resta
 *                           protetto dal safety gate globale (EMAIL_TEST_MODE).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Section, Card, Button, Alert, Input, Label, Badge } from "../design-system";
import { useAuth } from "../auth/AuthProvider";
import { X, Plus, Eye, Send, Save } from "lucide-react";

// ---------------------------------------------------------------------------
// Root — tabs
// ---------------------------------------------------------------------------
export function AdminNotificationsConfigPage() {
  const [tab, setTab] = useState("office");

  return (
    <Section
      title="Notifiche email — Admin"
      subtitle="Destinatari ufficio ordini e configurazione template per evento."
      actions={<Link to="/admin"><Button size="sm" data-testid="admin-notif-back">← Home Admin</Button></Link>}
      data-testid="admin-notifications-config-page"
    >
      <div role="tablist" aria-label="Sezioni notifiche"
           style={{ display: "flex", gap: 6, borderBottom: "1px solid var(--ds-color-border,#e5e7eb)",
                    marginBottom: 16 }}>
        <TabBtn active={tab === "office"} onClick={() => setTab("office")}
                testid="admin-notif-tab-office">Ufficio ordini</TabBtn>
        <TabBtn active={tab === "events"} onClick={() => setTab("events")}
                testid="admin-notif-tab-events">Eventi email</TabBtn>
        <TabBtn active={tab === "pilot"} onClick={() => setTab("pilot")}
                testid="admin-notif-tab-pilot">Clienti pilota</TabBtn>
      </div>

      {tab === "office" && <OfficePanel />}
      {tab === "events" && <EventsPanel />}
      {tab === "pilot" && <PilotPanel />}
    </Section>
  );
}

function TabBtn({ active, onClick, testid, children }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      data-testid={testid}
      onClick={onClick}
      style={{
        padding: "8px 14px", background: "transparent", border: "none",
        borderBottom: active ? "2px solid var(--ds-color-primary,#2563eb)" : "2px solid transparent",
        color: active ? "var(--ds-color-primary,#2563eb)" : "inherit",
        fontWeight: active ? 600 : 500,
        cursor: "pointer",
      }}
    >{children}</button>
  );
}

// ---------------------------------------------------------------------------
// Panel 1 — Ufficio ordini (INVARIATO logicamente)
// ---------------------------------------------------------------------------
function OfficePanel() {
  const { apiGet, token } = useAuth();
  const [emails, setEmails] = useState(null);
  const [fallback, setFallback] = useState("");
  const [fallbackDirty, setFallbackDirty] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null);
  const backend = process.env.REACT_APP_BACKEND_URL || "";

  const load = useCallback(async () => {
    setErr(null);
    try {
      const d = await apiGet("/api/admin/notifications/config");
      setEmails(d.order_office_emails || []);
      setFallback(d.commercial_fallback_email || "");
      setFallbackDirty(false);
    } catch (e) { setErr(e?.message || String(e)); setEmails([]); }
  }, [apiGet]);

  useEffect(() => { load(); }, [load]);

  const save = async (nextEmails, nextFallback) => {
    setBusy(true); setErr(null);
    try {
      const res = await fetch(`${backend}/api/admin/notifications/config`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token || ""}` },
        body: JSON.stringify({ emails: nextEmails, commercial_fallback_email: nextFallback ?? null }),
      });
      if (!res.ok) throw new Error(await res.text());
      const d = await res.json();
      setEmails(d.order_office_emails || []);
      setFallback(d.commercial_fallback_email || "");
      setFallbackDirty(false);
      setFlash("Salvato"); setTimeout(() => setFlash(null), 1500);
    } catch (e) { setErr(e?.message || String(e)); }
    finally { setBusy(false); }
  };

  const addEmail = async () => {
    const e = newEmail.trim().toLowerCase();
    if (!e || !e.includes("@")) return;
    if (emails.includes(e)) { setNewEmail(""); return; }
    await save([...emails, e], fallback);
    setNewEmail("");
  };
  const removeEmail = async (e) => { await save(emails.filter((x) => x !== e), fallback); };
  const saveFallback = async () => { await save(emails, (fallback || "").trim().toLowerCase() || null); };

  return (
    <div data-testid="admin-notif-office-panel">
      {err && <Alert tone="danger">Errore: {String(err)}</Alert>}
      {flash && <Alert tone="success">{flash}</Alert>}
      <Card>
        <Card.Header><strong>Destinatari Ufficio Ordini</strong></Card.Header>
        <Card.Body>
          <p className="muted" style={{ marginTop: 0 }}>
            Ogni indirizzo qui elencato riceverà una notifica separata (via Outbox)
            ad ogni conferma ordine del cliente. Le email dell'agente sono gestite
            automaticamente in base all'agente assegnato al cliente.
          </p>
          {!emails ? (
            <p className="muted">Caricamento…</p>
          ) : emails.length === 0 ? (
            <p className="muted"><em>Nessun indirizzo configurato.</em></p>
          ) : (
            <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
              {emails.map((e) => (
                <li key={e} data-testid={`office-email-${e}`}
                    style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                             padding: "6px 0", borderBottom: "1px solid #eee" }}>
                  <span>{e}</span>
                  <Button size="sm" onClick={() => removeEmail(e)}
                          loading={busy} disabled={busy}
                          data-testid={`office-email-remove-${e}`}>
                    <X size={12} /> Rimuovi
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <div style={{ flex: 1 }}>
              <Label htmlFor="new-email">Aggiungi indirizzo</Label>
              <Input id="new-email" type="email" value={newEmail}
                     data-testid="office-email-input"
                     onChange={(e) => setNewEmail(e.target.value)}
                     placeholder="ufficio@azienda.it"
                     onKeyDown={(e) => { if (e.key === "Enter") addEmail(); }} />
            </div>
            <div style={{ alignSelf: "flex-end" }}>
              <Button variant="primary" onClick={addEmail}
                      loading={busy} disabled={busy || !newEmail.trim()}
                      data-testid="office-email-add">
                <Plus size={14} /> Aggiungi
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>
      <Card style={{ marginTop: 16 }} data-testid="commercial-fallback-card">
        <Card.Header><strong>Email commerciale di fallback per Payment Hold</strong></Card.Header>
        <Card.Body>
          <p className="muted" style={{ marginTop: 0 }}>
            Usata solo quando il cliente non ha un agente assegnato o l'agente non ha un indirizzo email.
            Lasciare vuoto per non inviare alcuna notifica in questi casi (l'ordine viene comunque messo in Payment Hold e l'operazione è auditata).
          </p>
          <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "flex-end" }}>
            <div style={{ flex: 1 }}>
              <Label htmlFor="fallback-email">Indirizzo commerciale (facoltativo)</Label>
              <Input id="fallback-email" type="email" value={fallback || ""}
                     data-testid="fallback-email-input"
                     onChange={(e) => { setFallback(e.target.value); setFallbackDirty(true); }}
                     placeholder="commerciale@azienda.it" />
            </div>
            <Button variant="primary" onClick={saveFallback}
                    loading={busy} disabled={busy || !fallbackDirty}
                    data-testid="fallback-email-save">
              Salva fallback
            </Button>
            {fallback && (
              <Button size="sm"
                      onClick={() => { setFallback(""); setFallbackDirty(true); }}
                      loading={busy} disabled={busy}
                      data-testid="fallback-email-clear">
                Rimuovi
              </Button>
            )}
          </div>
        </Card.Body>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panel 2 — Eventi email (Mar 2026 fase 1)
// ---------------------------------------------------------------------------
const SEND_MODES = ["DISABLED", "TEST", "PILOT", "LIVE"];
const CC_TYPES = ["", "SALES", "OFFICE", "LOGISTICS", "FIXED_EMAIL"]; // "" = nessuno

const sendModeTone = (m) => ({
  DISABLED: "neutral", TEST: "info", PILOT: "warning", LIVE: "danger",
}[m] || "neutral");

function EventsPanel() {
  const { apiGet, apiPatch, apiPost } = useAuth();
  const [events, setEvents] = useState(null);   // array
  const [selectedKey, setSelectedKey] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  const [err, setErr] = useState(null);
  const [flash, setFlash] = useState(null);

  const load = useCallback(async () => {
    setLoadingList(true); setErr(null);
    try {
      const d = await apiGet("/api/admin/email-events");
      setEvents(Array.isArray(d) ? d : []);
      if (!selectedKey && Array.isArray(d) && d.length) setSelectedKey(d[0].event_key);
    } catch (e) {
      setErr(e?.message || String(e));
      setEvents([]);
    } finally { setLoadingList(false); }
  }, [apiGet, selectedKey]);

  useEffect(() => { load(); }, []); // eslint-disable-line

  const selected = useMemo(
    () => (events || []).find((x) => x.event_key === selectedKey) || null,
    [events, selectedKey]
  );

  const upsertEvent = (updated) => {
    setEvents((prev) => (prev || []).map((e) => (e.event_key === updated.event_key ? updated : e)));
  };

  return (
    <div data-testid="admin-notif-events-panel">
      {err && <Alert tone="danger" data-testid="events-error">Errore: {String(err)}</Alert>}
      {flash && <Alert tone="success" data-testid="events-flash">{flash}</Alert>}

      <Alert tone="info" data-testid="events-safety-note">
        <strong>Safety gate globale attivo.</strong> La modalità <em>send mode</em> per evento
        è configurativa. Il routing operativo resta protetto: nessuna email raggiunge cliente,
        agente o vettore reale finché <code>EMAIL_TEST_MODE</code> è ON. Il valore <strong>LIVE</strong>
        non è armato: verrà attivato in una fase successiva con doppio-gate.
        {" · "}<strong>PILOT</strong>: invio reale ai soli clienti pilota autorizzati
        (vedi tab &laquo;Clienti pilota&raquo;). Tutti gli altri destinatari restano in TEST.
      </Alert>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(240px, 320px) 1fr", gap: 16, marginTop: 12 }}
           className="admin-events-grid">
        {/* -------- Lista eventi -------- */}
        <Card data-testid="events-list-card">
          <Card.Header><strong>Eventi</strong></Card.Header>
          <Card.Body style={{ padding: 0 }}>
            {loadingList ? (
              <p className="muted" style={{ padding: 12 }}>Caricamento…</p>
            ) : !events || events.length === 0 ? (
              <p className="muted" style={{ padding: 12 }}><em>Nessun evento disponibile.</em></p>
            ) : (
              <ul style={{ margin: 0, padding: 0, listStyle: "none", maxHeight: 520, overflow: "auto" }}>
                {events.map((e) => {
                  const active = e.event_key === selectedKey;
                  const custom = !!(e.subject_template && e.body_template);
                  return (
                    <li key={e.event_key}>
                      <button
                        type="button"
                        data-testid={`event-item-${e.event_key}`}
                        onClick={() => setSelectedKey(e.event_key)}
                        style={{
                          width: "100%", textAlign: "left", padding: "10px 12px",
                          background: active ? "color-mix(in srgb, var(--ds-color-primary,#2563eb) 8%, transparent)" : "transparent",
                          border: "none",
                          borderLeft: active ? "3px solid var(--ds-color-primary,#2563eb)" : "3px solid transparent",
                          borderBottom: "1px solid #eee",
                          cursor: "pointer", fontFamily: "inherit",
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                          <strong style={{ fontSize: 14 }}>{e.name}</strong>
                          <Badge tone={sendModeTone(e.send_mode)}
                                 data-testid={`event-badge-${e.event_key}`}>
                            {e.send_mode}
                          </Badge>
                        </div>
                        <div style={{ display: "flex", gap: 6, marginTop: 4, flexWrap: "wrap", alignItems: "center" }}>
                          <code style={{ fontSize: 11, color: "#64748b" }}>{e.event_key}</code>
                          {!e.enabled && <Badge tone="neutral">off</Badge>}
                          <Badge tone={custom ? "success" : "neutral"}>
                            {custom ? "template custom" : "template default"}
                          </Badge>
                          <span style={{ fontSize: 11, color: "#64748b" }}>→ {e.recipient_type || "—"}</span>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card.Body>
        </Card>

        {/* -------- Editor -------- */}
        <div>
          {!selected ? (
            <Card><Card.Body><p className="muted">Seleziona un evento a sinistra.</p></Card.Body></Card>
          ) : (
            <EventEditor
              key={selected.event_key}
              event={selected}
              apiPatch={apiPatch}
              apiPost={apiPost}
              onSaved={(updated) => { upsertEvent(updated); setFlash("Configurazione salvata"); setTimeout(() => setFlash(null), 1800); }}
              onError={(msg) => setErr(msg)}
              onInfo={(msg) => { setFlash(msg); setTimeout(() => setFlash(null), 2200); }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Editor per singolo evento
// ---------------------------------------------------------------------------
function EventEditor({ event, apiPatch, apiPost, onSaved, onError, onInfo }) {
  const [enabled, setEnabled] = useState(event.enabled);
  const [sendMode, setSendMode] = useState(event.send_mode);
  const [recipientType, setRecipientType] = useState(event.recipient_type || event.allowed_recipient_types[0] || "");
  const [ccType, setCcType] = useState(event.cc_type || "");
  const [ccAddressesRaw, setCcAddressesRaw] = useState((event.cc_addresses || []).join(", "));
  const [subject, setSubject] = useState(event.subject_template || "");
  const [body, setBody] = useState(event.body_template || "");
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [testing, setTesting] = useState(false);
  const [preview, setPreview] = useState(null); // { subject, html }
  const [localErr, setLocalErr] = useState(null);

  const subjectRef = useRef(null);
  const bodyRef = useRef(null);
  const activeFieldRef = useRef("body"); // "subject" | "body"

  const insertVariable = (v) => {
    const token = `{{${v}}}`;
    const which = activeFieldRef.current;
    const target = which === "subject" ? subjectRef.current : bodyRef.current;
    const setter = which === "subject" ? setSubject : setBody;
    const currentVal = which === "subject" ? subject : body;
    if (target && typeof target.selectionStart === "number") {
      const s = target.selectionStart, e = target.selectionEnd;
      const next = currentVal.slice(0, s) + token + currentVal.slice(e);
      setter(next);
      requestAnimationFrame(() => {
        target.focus();
        const pos = s + token.length;
        try { target.setSelectionRange(pos, pos); } catch (_) {}
      });
    } else {
      setter(currentVal + token);
    }
  };

  const parseCcList = () => {
    const items = (ccAddressesRaw || "").split(/[,\n;]+/).map((x) => x.trim()).filter(Boolean);
    return items;
  };

  const doSave = async () => {
    setSaving(true); setLocalErr(null);
    try {
      const body_payload = {
        enabled,
        send_mode: sendMode,
        // Sending "" per il subject/body permette al backend di riportarli a NULL
        // (fallback ai template Python). Sending null invece li lascia invariati.
        subject_template: subject,
        body_template: body,
        recipient_type: recipientType,
        cc_type: ccType || "",
        cc_addresses: parseCcList(),
      };
      const updated = await apiPatch(`/api/admin/email-events/${encodeURIComponent(event.event_key)}`, body_payload);
      onSaved(updated);
    } catch (e) {
      const msg = extractErrMsg(e);
      setLocalErr(msg);
      onError && onError(msg);
    } finally { setSaving(false); }
  };

  const doPreview = async () => {
    setPreviewing(true); setLocalErr(null); setPreview(null);
    try {
      // Salva se ci sono modifiche non ancora persistite? No: preview usa lo stato
      // persistito. Se serve, mostrare un banner. Manteniamo il pattern richiesto
      // (preview usa server-side state).
      const p = await apiPost(`/api/admin/email-events/${encodeURIComponent(event.event_key)}/preview`, {});
      setPreview(p);
    } catch (e) {
      const msg = extractErrMsg(e);
      setLocalErr(msg);
      onError && onError(msg);
    } finally { setPreviewing(false); }
  };

  const doTestSend = async () => {
    setTesting(true); setLocalErr(null);
    try {
      const r = await apiPost(`/api/admin/email-events/${encodeURIComponent(event.event_key)}/test-send`, {});
      onInfo && onInfo(`Email di prova inviata al destinatario TEST (${r?.to || "test"}). Il cliente reale NON è stato contattato.`);
    } catch (e) {
      const msg = extractErrMsg(e);
      setLocalErr(msg);
      onError && onError(msg);
    } finally { setTesting(false); }
  };

  const recipientOptions = event.allowed_recipient_types || [];
  const busy = saving || previewing || testing;

  return (
    <Card data-testid="event-editor">
      <Card.Header>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
          <div>
            <strong data-testid="event-editor-name">{event.name}</strong>
            <div style={{ fontSize: 12, color: "#64748b" }}>
              <code>{event.event_key}</code> · {event.description}
            </div>
          </div>
          <Badge tone={sendModeTone(sendMode)} data-testid="event-editor-mode-badge">{sendMode}</Badge>
        </div>
      </Card.Header>
      <Card.Body>
        {localErr && <Alert tone="danger" data-testid="event-editor-error">{localErr}</Alert>}

        {/* Row 1 — Enabled + Send Mode */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
          <div>
            <Label htmlFor="ev-enabled">Attivo</Label>
            <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
              <input id="ev-enabled" type="checkbox" checked={enabled}
                     onChange={(e) => setEnabled(e.target.checked)}
                     data-testid="event-editor-enabled" />
              <span style={{ fontSize: 13, color: "#64748b" }}>
                Se disattivato, il backend non emette l'evento nemmeno in TEST.
              </span>
            </label>
          </div>
          <div>
            <Label htmlFor="ev-send-mode">Send mode</Label>
            <select id="ev-send-mode" className="ds-input" value={sendMode}
                    onChange={(e) => setSendMode(e.target.value)}
                    data-testid="event-editor-send-mode">
              {SEND_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            {sendMode === "LIVE" && (
              <p style={{ fontSize: 12, color: "var(--ds-color-warning,#b45309)", marginTop: 4 }}>
                LIVE non è ancora armato: il safety gate globale ha priorità.
              </p>
            )}
            {sendMode === "PILOT" && (
              <p style={{ fontSize: 12, color: "var(--ds-color-info,#0369a1)", marginTop: 4 }}
                 data-testid="event-editor-pilot-hint">
                PILOT · Clienti pilota attivi: <strong>{event.pilot_customer_count ?? 0}</strong> —
                invio reale solo ai clienti pilota autorizzati.
              </p>
            )}
          </div>
        </div>

        {/* Row 2 — Recipient + CC type + CC addresses */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
          <div>
            <Label htmlFor="ev-recipient">Destinatario principale</Label>
            <select id="ev-recipient" className="ds-input" value={recipientType}
                    onChange={(e) => setRecipientType(e.target.value)}
                    data-testid="event-editor-recipient-type">
              {recipientOptions.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <p style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>
              Solo i tipi ammessi per l'evento. Backend valida.
            </p>
          </div>
          <div>
            <Label htmlFor="ev-cc-type">CC (tipo)</Label>
            <select id="ev-cc-type" className="ds-input" value={ccType}
                    onChange={(e) => setCcType(e.target.value)}
                    data-testid="event-editor-cc-type">
              <option value="">(nessuno)</option>
              {CC_TYPES.filter(Boolean).map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <Label htmlFor="ev-cc-addr">Indirizzi CC fissi (separati da virgola)</Label>
          <Input id="ev-cc-addr" type="text" value={ccAddressesRaw}
                 data-testid="event-editor-cc-addresses"
                 onChange={(e) => setCcAddressesRaw(e.target.value)}
                 placeholder="es. ufficio@azienda.it, controllo@azienda.it" />
        </div>

        {/* Variabili disponibili */}
        <div style={{ marginBottom: 8 }}>
          <Label>Variabili disponibili</Label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 4 }} data-testid="event-editor-variables">
            {(event.variables || []).map((v) => (
              <button
                key={v} type="button"
                onClick={() => insertVariable(v)}
                title={`Inserisci {{${v}}} nel campo attivo`}
                data-testid={`event-var-${v}`}
                style={{
                  fontFamily: "monospace", fontSize: 12, padding: "3px 8px",
                  background: "color-mix(in srgb, var(--ds-color-info,#0ea5e9) 12%, white)",
                  color: "var(--ds-color-info,#0ea5e9)",
                  border: "1px solid color-mix(in srgb, var(--ds-color-info,#0ea5e9) 30%, transparent)",
                  borderRadius: 4, cursor: "pointer",
                }}
              >{"{{"}{v}{"}}"}</button>
            ))}
          </div>
          <p style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>
            Clicca su una variabile per inserirla nel campo attivo (Oggetto o Corpo).
            Lasciare Oggetto e Corpo vuoti per usare il template Python di default.
          </p>
        </div>

        {/* Subject */}
        <div style={{ marginBottom: 12 }}>
          <Label htmlFor="ev-subject">Oggetto</Label>
          <input
            id="ev-subject" type="text" value={subject} ref={subjectRef}
            className="ds-input"
            data-testid="event-editor-subject"
            onFocus={() => { activeFieldRef.current = "subject"; }}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="es. Ordine {{order_number}} confermato" />
        </div>

        {/* Body */}
        <div style={{ marginBottom: 12 }}>
          <Label htmlFor="ev-body">Corpo email (HTML)</Label>
          <textarea
            id="ev-body" ref={bodyRef} value={body}
            data-testid="event-editor-body"
            onFocus={() => { activeFieldRef.current = "body"; }}
            onChange={(e) => setBody(e.target.value)}
            rows={10}
            className="ds-input"
            style={{ width: "100%", fontFamily: "monospace", fontSize: 13, resize: "vertical" }}
            placeholder="<p>Ciao {{customer_name}}, il tuo ordine {{order_number}} è confermato.</p>" />
        </div>

        {/* Actions */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "flex-end" }}>
          <Button variant="secondary" onClick={doPreview}
                  loading={previewing} disabled={busy}
                  data-testid="event-editor-preview">
            <Eye size={14} /> Anteprima
          </Button>
          <Button variant="secondary" onClick={doTestSend}
                  loading={testing} disabled={busy}
                  data-testid="event-editor-test-send">
            <Send size={14} /> Invia email di prova
          </Button>
          <Button variant="primary" onClick={doSave}
                  loading={saving} disabled={busy}
                  data-testid="event-editor-save">
            <Save size={14} /> Salva configurazione
          </Button>
        </div>
      </Card.Body>

      {preview && (
        <PreviewModal preview={preview} onClose={() => setPreview(null)} />
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Preview modal
// ---------------------------------------------------------------------------
function PreviewModal({ preview, onClose }) {
  useEffect(() => {
    const onEsc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [onClose]);

  return (
    <div role="dialog" aria-modal="true" data-testid="event-preview-modal"
         style={{
           position: "fixed", inset: 0, background: "rgba(0,0,0,.45)",
           display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
           padding: 16,
         }}
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: "white", borderRadius: 8, width: "100%", maxWidth: 720,
                    maxHeight: "90vh", display: "flex", flexDirection: "column",
                    boxShadow: "0 10px 40px rgba(0,0,0,.25)" }}>
        <div style={{ padding: "12px 16px", borderBottom: "1px solid #eee",
                      display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <strong>Anteprima email</strong>
          <Button size="sm" onClick={onClose} data-testid="event-preview-close">
            <X size={14} /> Chiudi
          </Button>
        </div>
        <div style={{ padding: 16, overflow: "auto" }}>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5 }}>Oggetto</div>
            <div data-testid="event-preview-subject" style={{ fontWeight: 600 }}>{preview.subject || <em>(vuoto)</em>}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Corpo</div>
            <div data-testid="event-preview-body"
                 style={{ border: "1px solid #eee", borderRadius: 6, padding: 12, background: "#fafafa" }}
                 // Anteprima renderizzata: il corpo arriva già dal backend/fallback Python.
                 dangerouslySetInnerHTML={{ __html: preview.html || "" }} />
          </div>
          <p style={{ fontSize: 11, color: "#64748b", marginTop: 12 }}>
            Anteprima renderizzata con dati di esempio. Nessuna email inviata.
          </p>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panel 3 — Clienti pilota
// ---------------------------------------------------------------------------
function PilotPanel() {
  const { apiGet, apiPost, apiDelete } = useAuth();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [avail, setAvail] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [flash, setFlash] = useState(null);

  const load = useCallback(async () => {
    setErr(null);
    try { setRows(await apiGet("/api/admin/email-pilot-customers")); }
    catch (e) { setErr(e?.message || String(e)); setRows([]); }
  }, [apiGet]);
  useEffect(() => { load(); }, [load]);

  const search = async (query) => {
    setQ(query);
    try {
      const r = await apiGet(`/api/admin/email-pilot-customers/available?q=${encodeURIComponent(query || "")}`);
      setAvail(Array.isArray(r) ? r : []);
    } catch (e) { setErr(e?.message || String(e)); }
  };

  const add = async (customer_id) => {
    setBusy(true); setErr(null);
    try {
      await apiPost("/api/admin/email-pilot-customers", { customer_id });
      setFlash("Cliente aggiunto all'allowlist"); setTimeout(() => setFlash(null), 1800);
      await Promise.all([load(), search(q)]);
    } catch (e) { setErr(extractErrMsg(e)); }
    finally { setBusy(false); }
  };

  const remove = async (customer_id) => {
    setBusy(true); setErr(null);
    try {
      await apiDelete(`/api/admin/email-pilot-customers/${customer_id}`);
      setFlash("Cliente rimosso dall'allowlist"); setTimeout(() => setFlash(null), 1800);
      await Promise.all([load(), search(q)]);
    } catch (e) { setErr(extractErrMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-notif-pilot-panel">
      {err && <Alert tone="danger" data-testid="pilot-error">Errore: {String(err)}</Alert>}
      {flash && <Alert tone="success" data-testid="pilot-flash">{flash}</Alert>}
      <Alert tone="warning" data-testid="pilot-safety-note">
        <strong>Pilot routing.</strong> I clienti in questa lista riceveranno email reali
        solo per gli eventi con <code>send_mode=PILOT</code>. Tutti gli altri destinatari
        (agenti, ufficio, logistica, vettori) restano in TEST anche se il send mode
        dell'evento è PILOT. LIVE resta disarmato.
      </Alert>

      <Card style={{ marginTop: 10 }}>
        <Card.Header><strong>Clienti pilota abilitati</strong> ({rows ? rows.length : "…"})</Card.Header>
        <Card.Body>
          {!rows ? <p className="muted">Caricamento…</p> :
            rows.length === 0 ? <p className="muted"><em>Nessun cliente in allowlist.</em></p> :
            <table className="ds-table" style={{ fontSize: 13, width: "100%" }} data-testid="pilot-table">
              <thead><tr><th>Codice</th><th>Ragione sociale</th><th>Email</th><th>Attivo</th><th>Utente CUSTOMER</th><th>Abilitato il</th><th></th></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.customer_id} data-testid={`pilot-row-${r.customer_code}`}>
                    <td><code>{r.customer_code}</code></td>
                    <td>{r.business_name}</td>
                    <td>{r.customer_email || <em className="muted">—</em>}</td>
                    <td>{r.customer_active ? <Badge tone="success">Sì</Badge> : <Badge tone="neutral">No</Badge>}</td>
                    <td>{r.has_customer_user ? <Badge tone="info">presente</Badge> : <Badge tone="neutral">assente</Badge>}</td>
                    <td style={{ fontSize: 11, color: "#64748b" }}>{r.enabled_at ? new Date(r.enabled_at).toLocaleString("it-IT") : "—"}</td>
                    <td>
                      <Button size="sm" onClick={() => remove(r.customer_id)}
                              loading={busy} disabled={busy}
                              data-testid={`pilot-remove-${r.customer_code}`}>
                        <X size={12} /> Rimuovi
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>}
        </Card.Body>
      </Card>

      <Card style={{ marginTop: 12 }}>
        <Card.Header><strong>Aggiungi cliente</strong></Card.Header>
        <Card.Body>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
            <div style={{ flex: 1 }}>
              <Label htmlFor="pilot-search">Cerca (codice, ragione sociale, email)</Label>
              <Input id="pilot-search" type="text" value={q}
                     data-testid="pilot-search-input"
                     onChange={(e) => search(e.target.value)}
                     placeholder="es. SPEEDY, 0001048, info@…" />
            </div>
          </div>
          {avail.length > 0 && (
            <table className="ds-table" style={{ marginTop: 10, fontSize: 13, width: "100%" }} data-testid="pilot-available-table">
              <thead><tr><th>Codice</th><th>Ragione sociale</th><th>Email</th><th>Utente</th><th></th></tr></thead>
              <tbody>
                {avail.map((a) => (
                  <tr key={a.customer_id} data-testid={`pilot-avail-${a.customer_code}`}>
                    <td><code>{a.customer_code}</code></td>
                    <td>{a.business_name}</td>
                    <td>{a.customer_email || <em className="muted">—</em>}</td>
                    <td>{a.has_customer_user ? <Badge tone="info">sì</Badge> : <Badge tone="neutral">no</Badge>}</td>
                    <td>
                      {a.in_allowlist ? (
                        <Badge tone="success">Già in allowlist</Badge>
                      ) : (
                        <Button size="sm" variant="primary"
                                onClick={() => add(a.customer_id)}
                                loading={busy} disabled={busy || !a.has_customer_user}
                                data-testid={`pilot-add-${a.customer_code}`}>
                          <Plus size={12} /> Abilita al pilot
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}


function extractErrMsg(e) {
  if (!e) return "Errore sconosciuto";
  if (typeof e === "string") return e;
  // Axios-like
  const detail = e?.response?.data?.detail;
  if (detail) return typeof detail === "string" ? detail : JSON.stringify(detail);
  return e.message || String(e);
}
